<script setup lang="ts" generic="T">
import { computed } from 'vue';
import StepperDivider from '@apparatix/library/StepperDivider.vue';

interface StepperProps {
  steps: T[];
  labelField: keyof T;
  currentStep: number;
  completeBackground?: string;
  completeColor?: string;
  incompleteBackground?: string;
  incompleteColor?: string;
}

const props = withDefaults(defineProps<StepperProps>(), {
  completeBackground: 'var(--primary-green)',
  completeColor: 'var(--white)',
  incompleteBackground: 'var(--light-neutral-background)',
  incompleteColor: 'var(--swimlane-secondary-text)',
  labelField: null,
});

const stepLabels = computed<string[]>(() => props.steps.map(step => {
  if (typeof step[props.labelField] !== 'string') {
    throw new Error(`Invalid type in Step label field. Expected string, but got ${typeof step[props.labelField]}`);
  }
  return step[props.labelField] as string;
}));

const calculateStepColors = (currentIndex: number) => {
  if (currentIndex <= props.currentStep) {
    return {
      background: props.completeBackground,
      color: props.completeColor,
    };
  }

  return {
    background: props.incompleteBackground,
    color: props.incompleteColor,
  };
};

const calculateDividerColors = (currentIndex: number) => {
  const { length } = stepLabels.value;
  const isComplete = currentIndex <= props.currentStep;

  let nextIndex: number;
  if (currentIndex === length - 1) {
    nextIndex = currentIndex;
  } else {
    nextIndex = currentIndex + 1;
  }
  const isNextComplete = nextIndex <= props.currentStep;

  return {
    leftColor: isComplete ? props.completeBackground : props.incompleteBackground,
    dividerColor: props.completeColor,
    rightColor: isNextComplete ? props.completeBackground : props.incompleteBackground,
  };
};
</script>
<template>
  <div class="flex h-[28px] w-full">
    <div class="lg:hidden rounded-l-lg min-w-[25px] h-full" :style="{background: props.completeBackground}" />
    <StepperDivider
      class="lg:hidden"
      :left-color="props.completeBackground"
      :divider-color="props.completeColor"
      :right-color="props.completeBackground"
    />
    <template v-for="(step, idx) in stepLabels" :key="idx">
      <div
        class="flex grow items-center justify-center px-2"
        :class="{['lg:rounded-l-lg']: idx === 0, ['lg:rounded-r-lg']: idx === props.steps.length - 1}"
        :style="calculateStepColors(idx)"
      >
        <p class="font-semibold text-sm lg:text-base" style="text-wrap: nowrap;">
          {{ step }}
        </p>
      </div>
      <StepperDivider
        v-if="idx < props.steps.length - 1"
        v-bind="calculateDividerColors(idx)"
      />
    </template>
    <StepperDivider
      class="lg:hidden"
      v-bind="calculateDividerColors(stepLabels.length - 1)"
    />
    <div class="lg:hidden rounded-r-lg min-w-[25px] h-full" :style="{background: props.currentStep === stepLabels.length - 1 ? props.completeBackground : props.incompleteBackground}" />
  </div>
</template>
